<template>
  <v-container>
    <v-card class="pa-2 mb-4">
      <v-tooltip :disabled="canCreate" bottom>
        <v-btn
          slot="activator"
          :disabled="!canCreate"
          @click="showAddDialog = true"
          color="primary">
          {{ $vuetify.t('$vuetify.GroupsUsers.add') }}
        </v-btn>
        <span>{{ $vuetify.t('$vuetify.Permissions.noPermission') }}</span>
      </v-tooltip>

      <v-tooltip :disabled="canCreate" bottom>
        <v-btn slot="activator"
               :disabled="!canCreate"
               @click="showCSVDialog = true">
          <v-icon left>cloud_upload</v-icon>
          {{ $vuetify.t('$vuetify.AdminUsers.csvDialog') }}
        </v-btn>
        <span>{{ $vuetify.t('$vuetify.Permissions.noPermission') }}</span>
      </v-tooltip>

      <v-btn to="/admin/password-printout">
        <v-icon left>print</v-icon>
        {{ $vuetify.t('$vuetify.AdminUsers.passwordlist') }}
      </v-btn>
    </v-card>

    <v-card class="pa-3 mb-2">
      <div>
        <v-toolbar>
          <v-toolbar-title>{{ users.length }} {{ $vuetify.t('$vuetify.AdminUsers.users') }}</v-toolbar-title>
          <v-spacer style="flex-grow: 5 !important;"></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="search"
            :label="$vuetify.t('$vuetify.AdminUsers.searchLabel')"
            class="pt-0"
            single-line
            hide-details
          ></v-text-field>
        </v-toolbar>

        <confirm-dialog :show="bulkDeleting != null" v-on:confirmed="bulkDelete(true)"
                        v-on:closed="bulkDeleting = null" confirm-btn-color="red"
                        confirm-btn-text="$vuetify.AdminUsers.deleteConfirm"
                        cancel-btn-text="$vuetify.AdminUsers.deleteCancel">
          <h3>{{ $vuetify.t('$vuetify.AdminUsers.deleteBulkUsersQuestion') }}</h3>
          <div class="user-listing delete-users">
            <span v-for="user in bulkDeleting">{{ user }}</span>
          </div>
        </confirm-dialog>

        <confirm-dialog :show="toDelete != null" :full="true">
          <v-card-title>
            <h3>{{ $vuetify.t('$vuetify.AdminUsers.deleteHeader') }}</h3>
          </v-card-title>
          <v-card-text v-if="toDelete !== null">
            <p>
              {{ $vuetify.t('$vuetify.AdminUsers.deleteText', getUserById(this.toDelete).username) }}
            </p>
          </v-card-text>
          <v-card-actions>
            <v-btn
              @click="deleteUser"
              color="error"
            >{{ $vuetify.t('$vuetify.AdminUsers.deleteConfirm') }}
            </v-btn>
            <v-btn @click="reset">{{ $vuetify.t('$vuetify.AdminUsers.formCancel') }}</v-btn>
          </v-card-actions>
        </confirm-dialog>

        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="users"
          :loading="isLoading"
          :rows-per-page-items="pageRowCount"
          :search="search"
          class="elevation-1">
          <template slot="items" slot-scope="props">
            <td>
              <v-checkbox :disabled="!props.item.showCheckbox" v-model="selected" :value="props.item.id"
                          style="margin:0px;padding:0px" hide-details/>
            </td>
            <td>{{ props.item.username }}</td>
            <td>{{ props.item.first_name }}</td>
            <td>{{ props.item.last_name }}</td>
            <td>
              <ul v-if="props.item.roles.length >= 1" style="padding:0;">
                <li v-for="role in props.item.roles">
                    <span v-if="role[0] != null && role[2] != null">
                      {{ role[0] + ' in ' + role[2] }}
                    </span>
                  <span v-if="role[0] != null && role[2] == null">
                      {{ role[0] }}
                    </span>
                </li>
              </ul>
              <span v-else>{{ $vuetify.t('$vuetify.rolesCompound.noRoles') }}</span>
            </td>
            <td>
              {{ $vuetify.t('$vuetify.AdminUsers.columnHasSetPassword-' + props.item.hasSetPassword) }}
            </td>
            <td style="white-space: nowrap;">
              <v-icon v-if="canEdit" small class="mr-2" @click="edit(props.item.id)">edit</v-icon>
              <v-icon v-if="canEdit" small class="mr-2" @click="editRole(props.item.id)">group</v-icon>
              <v-icon v-if="canResetPassword" small class="mr-2" @click="resetPassword(props.item.id)">password</v-icon>
              <v-icon v-if="canDelete" small class="" @click="toDelete = props.item.id">delete</v-icon>
            </td>
          </template>

          <template slot="footer">
            <td :colspan="headers.length">
              <v-icon style="transform: rotate(90deg); margin-left: 3px;">subdirectory_arrow_left</v-icon>
              <a @click="markAll()">{{ $vuetify.t('$vuetify.AdminUsers.selectAll') }}</a>
              <span>&nbsp;/&nbsp;</span>
              <a @click="unmarkAll()">{{ $vuetify.t('$vuetify.AdminUsers.unselectAll') }}</a>

              <span style="margin-left: 6rem;">{{ selected.length }}
                {{ $vuetify.t('$vuetify.AdminUsers.selected') }}:
              </span>
              <v-btn text small :disabled="selected.length < 1" @click="bulkDelete(false)">
                {{ $vuetify.t('$vuetify.AdminUsers.delete') }}
              </v-btn>
              <v-btn text small :disabled="selected.length < 1" @click="showBulkUserRoleAddDialog = true">
                {{ $vuetify.t('$vuetify.AdminUsers.addRole') }}
              </v-btn>
              <v-btn text small :disabled="selected.length < 1" @click="showBulkUserRoleRemoveDialog = true">
                {{ $vuetify.t('$vuetify.AdminUsers.removeRole') }}
              </v-btn>
            </td>
          </template>

          <v-alert
            slot="no-results"
            :value="true"
            color="info"
            icon="warning">
            {{ $vuetify.t('$vuetify.AdminUsers.searchNoResults', search) }}
          </v-alert>
        </v-data-table>

      </div>
    </v-card>

    <user-role-bulk-add-dialog
      v-if="showBulkUserRoleAddDialog"
      :show="showBulkUserRoleAddDialog"
      :users="selectedUsers"
      @handleClose="closeBulkAddUserRoleDialog"
      @reloadUsers="getUsers"
    />

    <user-role-bulk-remove-dialog
      v-if="showBulkUserRoleRemoveDialog"
      :show="showBulkUserRoleRemoveDialog"
      :users="selectedUsers"
      @handleClose="closeBulkRemoveUserRoleDialog"
      @reloadUsers="getUsers"
    />

    <user-edit-dialog
      v-if="toEdit"
      :show="showEditDialog"
      :user="toEdit"
      @handleClose="closeEditDialog"
      @reloadUsers="getUsers"
    />

    <user-password-reset-dialog
      v-if="toPasswordReset"
      :show="showPasswordResetDialog"
      :user="toPasswordReset"
      @handleClose="closePasswordResetDialog"
    />

    <user-add-dialog
      v-if="showAddDialog"
      :show="showAddDialog"
      :users="users"
      @handleClose="closeAddDialog"
      @reloadUsers="getUsers"
    />

    <user-role-edit-dialog
      v-if="toRoleEdit"
      :show="showRoleEditDialog"
      :user="toRoleEdit"
      @handleClose="closeRoleEditDialog"
      @reloadUsers="getUsers"
    />

    <user-c-s-v-import-dialog
      :show="showCSVDialog"
      :users="users"
      @handleClose="closeCsvDialog"
      @reloadUsers="getUsers"
    />

    <v-snackbar v-model="showSnackbar" :bottom="true">
      {{ snackbarMsg }}
      <v-btn
        color="pink"
        flat
        @click="showSnackbar = false"
      >{{ $vuetify.t('$vuetify.Snackbar.close') }}
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import api from '@/api'
import ConfirmDialog from './dialogs/ConfirmDialog'
import UserAddDialog from './dialogs/UserAddDialog'
import UserCSVImportDialog from './dialogs/UserCSVImportDialog'
import UserEditDialog from './dialogs/UserEditDialog'
import UserPasswordResetDialog from './dialogs/UserPasswordResetDialog'
import UserRoleEditDialog from './dialogs/UserRoleEditDialog'
import UserRoleBulkAddDialog from './dialogs/UserRoleBulkAddDialog'
import UserRoleBulkRemoveDialog from './dialogs/UserRoleBulkRemoveDialog'

export default {
  $_veeValidate: {validator: 'new'},
  name: 'GroupsUsers',
  components: {
    UserRoleBulkRemoveDialog,
    UserRoleBulkAddDialog,
    UserRoleEditDialog,
    UserPasswordResetDialog,
    UserEditDialog,
    UserCSVImportDialog,
    UserAddDialog,
    ConfirmDialog
  },
  data: function () {
    const defaultData = {
      usergroup: 'student',
      ideaSpace: null
    }

    return {
      headers: [
        {
          sortable: false,
          value: 'showCheckbox'
        },
        {
          text: this.$vuetify.t('$vuetify.AdminUsers.formLogin'),
          align: 'left',
          sortable: true,
          value: 'username'
        },
        {
          text: this.$vuetify.t('$vuetify.AdminUsers.formFirstName'),
          align: 'left',
          sortable: true,
          value: 'first_name'
        },
        {
          text: this.$vuetify.t('$vuetify.AdminUsers.formLastName'),
          align: 'left',
          sortable: true,
          value: 'last_name'
        },
        {
          text: this.$vuetify.t('$vuetify.AdminUsers.columnUserRole'),
          align: 'left',
          sortable: true,
          value: 'role_id'
        },
        {
          text: this.$vuetify.t('$vuetify.AdminUsers.columnHasSetPassword'),
          sortable: true,
          value: 'hasSetPassword'
        },
        {
          text: this.$vuetify.t('$vuetify.AdminUsers.columnActions'),
          align: 'center',
          value: null,
          sortable: false
        }
      ],
      isLoading: true,
      users: [],
      toEdit: null,
      toRoleEdit: null,
      toPasswordReset: null,
      toDelete: null,
      search: '',
      showSnackbar: false,
      snackbarMsg: '',
      selected: [],
      bulkDeleting: null,
      ideaSpaces: [],
      usergroup: defaultData.usergroup,
      ideaSpace: defaultData.ideaSpace,
      defaultData: defaultData,
      showAddDialog: false,
      showCSVDialog: false,
      showEditDialog: false,
      showRoleEditDialog: false,
      showPasswordResetDialog: false,
      showBulkUserRoleAddDialog: false,
      showBulkUserRoleRemoveDialog: false,
      pageRowCount: [15, 30, 50, {'text': '$vuetify.dataIterator.rowsPerPageAll', 'value': -1}]
    }
  },
  beforeMount: function () {
    this.getIdeaSpaces()
    this.getUsers()
  },
  computed: {
    canCreate: function () {
      return this.$hasPermission(this.$permissions.user.items.createUser)
    },
    canEdit: function () {
      return this.$hasPermission(this.$permissions.user.items.editUser)
    },
    canResetPassword: function () {
      return this.$isUserAdmin()
    },
    canDelete: function () {
      return this.$hasPermission(this.$permissions.user.items.deleteUser)
    },
    selectedUsers: function () {
      return this.users.filter(usr => this.selected.includes(usr.id))
    }
  },
  methods: {
    markAll: function () {
      const headers = this.headers.filter(h => h.value in this.users[0]).map(h => h.value)
      const search = this.search.toLowerCase()
      let i = 0
      let l = headers.length
      let val = ''
      this.selected = [...this.users].filter(user => {
        if (user.showCheckbox) {
          for (i = 0; i < l; ++i) {
            val = user[headers[i]]
            if (val != null && typeof val !== 'boolean' && val.toString().toLowerCase().indexOf(search) !== -1) {
              return true
            }
          }
        }
        return false
      }).map(a => a.id)
    },
    unmarkAll () {
      this.selected = []
    },
    edit (id) {
      this.toEdit = this.getUserById(id)
      this.showEditDialog = true
    },
    editRole (id) {
      this.toRoleEdit = this.getUserById(id)
      this.showRoleEditDialog = true
    },
    resetPassword (id) {
      this.toPasswordReset = this.getUserById(id)
      this.showPasswordResetDialog = true
    },
    closeEditDialog (value) {
      this.showEditDialog = value
      this.toEdit = null
    },
    closeRoleEditDialog (value) {
      this.showRoleEditDialog = value
      this.toRoleEdit = null
    },
    closePasswordResetDialog (value) {
      this.showPasswordResetDialog = value
      this.toPasswordReset = null
    },
    closeAddDialog (value) {
      this.showAddDialog = value
    },
    closeCsvDialog (value) {
      this.showCSVDialog = value
    },
    closeBulkAddUserRoleDialog (value) {
      this.showBulkUserRoleAddDialog = value
    },
    closeBulkRemoveUserRoleDialog (value) {
      this.showBulkUserRoleRemoveDialog = value
    },
    reset: function () {
      this.toEdit = null
      this.toDelete = null
      this.bulkDeleting = null
      this.usergroup = this.defaultData.usergroup
      this.ideaSpace = this.defaultData.ideaSpace
    },
    getUserById: function (id) {
      return this.users.filter(u => u.id === id).shift()
    },
    getUsers: function () {
      this.isLoading = true

      const parseConfig = entry => Object.assign({}, entry, {
        config: JSON.parse(entry.config)
      })

      // Groups are returned from Postgrest as a composite value, which means
      // they're strings that need to be unpacked
      const parseRoles = entry => Object.assign({}, entry, {
        roles: entry.roles === null
          ? []
          : entry.roles.map(val => {
            // Remove outer parantheses and split into
            // group_name, space_id, space_name
            return [val['f1'], val['f2'], val['f3']]
          })
      })

      const parseAdminRoles = entry => Object.assign({}, entry, {
        showCheckbox: !(entry.is_admin || entry.is_school_admin)
      })

      // Insert a boolean field that indicates whether the config field
      // contains a temporary password
      const checkTempPassword = entry => Object.assign({}, entry, {
        hasSetPassword: entry.config.temp_password == null
      })

      var schoolId
      if (this.$store.getters.selected_school) {
        schoolId = this.$store.getters.selected_school
      }

      api.user.getListing(schoolId)
        .then((res) => {
          this.users = res.data
            .map(parseConfig)
            .map(checkTempPassword)
            .map(parseRoles)
            .map(parseAdminRoles)
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    getIdeaSpaces: function () {
      const schoolId = this.$store.getters.selected_school
      api.ideaSpace.getIdeaSpaces(schoolId).then((res) => {
        // Return value is transformed so that it is suitable for
        // vuetify's select component
        this.ideaSpaces = res.data.map(sp => ({
          text: sp.title,
          value: sp.id
        }))
        // Add a no-space-assigned option here
        this.ideaSpaces.push({
          text: this.$vuetify.t('$vuetify.AdminUsers.formNoSpaceSelected'),
          value: null
        })
      })
    },
    deleteUser: function () {
      api.user.deleteUser(this.$store.getters.selected_school, this.toDelete)
        .then(res => {
          if (res.status < 400) {
            this.reset()
            this.getUsers()
            this.showSnackbar = true
            this.snackbarMsg = this.$vuetify.t(
              '$vuetify.AdminUsers.snackbarDeleted')
          } else {
            this.reset()
            this.showSnackbar = true
            this.snackbarMsg = this.$vuetify.t(
              '$vuetify.AdminUsers.snackbarDeleteFailed')
          }
        })
    },
    bulkDelete: function (confirmed) {
      if (!confirmed) {
        this.bulkDeleting = [...this.users].filter(u => this.selected.indexOf(u.id) !== -1).map(u => u.first_name + ' ' + u.last_name + ' (' + u.username + ')')
      } else {
        api.user.deleteUserBulk(this.$store.getters.selected_school, [...this.selected]).then(res => {
          if (res.status < 400) {
            this.getUsers()
            this.snackbarMsg = this.$vuetify.t(
              '$vuetify.AdminUsers.snackbarDeleted')
          } else {
            this.snackbarMsg = this.$vuetify.t(
              '$vuetify.AdminUsers.snackbarDeleteFailed')
          }
          this.reset()
        }).catch(res => {
          this.snackbarMsg = this.$vuetify.t(
            '$vuetify.AdminUsers.snackbarDeleteFailed')
        }).finally(() => {
          this.showSnackbar = true
        })
        this.selected = []
      }
    }
  }
}
</script>

<style scss>

.v-card__actions {
  padding: 16px
}

.v-datatable__actions {
  background-color: #f5f5f5 !important;
}

.user-listing.add-role,
.user-listing.remove-role {
  max-height: calc(100vh - 346px);
  overflow: auto;
}

.user-listing.delete-users {
  max-height: calc(100vh - 320px);
  overflow: auto;
}

.user-listing span::after {
  content: ', ';
  display: inline;
}

.user-listing span:last-child::after {
  content: '';
}
</style>
